import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import {
  avatarStyle, statusStyle, statusLabel, initials, fmtDate,
  INTRODUCED_BY, RELATIONSHIP_TYPES, CONTACT_KINDS, todayIso, userDisplayName,
} from '../directory';

function linkedinHref(url) {
  if (!url) return null;
  return url.startsWith('http') ? url : `https://${url}`;
}

function Section({ title, first, children }) {
  return (
    <div style={{ padding: '18px 28px', borderTop: first ? 'none' : '1px solid var(--border)' }}>
      {title && (
        <div style={{
          fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 10,
        }}>{title}</div>
      )}
      {children}
    </div>
  );
}

function InfoRow({ label, children }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, padding: '7px 0' }}>
      <span style={{ fontSize: 13, color: 'var(--text3)', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: 'var(--text)', textAlign: 'right' }}>{children}</span>
    </div>
  );
}

function ContactRow({ icon, label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0' }}>
      <span style={{ fontSize: 14, color: 'var(--text3)', width: 18, textAlign: 'center' }}>{icon}</span>
      <span style={{ fontSize: 13, color: 'var(--text3)' }}>{label}</span>
      <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text)', textAlign: 'right' }}>{children}</span>
    </div>
  );
}

const BADGE = {
  display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 600,
  padding: '3px 9px', borderRadius: 20, letterSpacing: '0.02em',
};

export default function DirectoryContactDetail({
  contact, user, canEdit, canDelete, onBack, onEdit, onDelete, onContactUpdated,
}) {
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [noteDate, setNoteDate] = useState(todayIso());
  const [addingNote, setAddingNote] = useState(false);

  const loadNotes = useCallback(async () => {
    setLoadingNotes(true);
    const { data } = await supabase
      .from('directory_contact_notes')
      .select('*')
      .eq('contact_id', contact.id)
      .order('note_date', { ascending: false })
      .order('created_at', { ascending: false });
    setNotes(data || []);
    setLoadingNotes(false);
  }, [contact.id]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const addNote = async () => {
    if (!noteText.trim() || addingNote) return;
    setAddingNote(true);
    const { error: e } = await supabase.from('directory_contact_notes').insert({
      contact_id: contact.id,
      note_date: noteDate,
      note_text: noteText.trim(),
      created_by: userDisplayName(user),
    });
    if (e) { setAddingNote(false); alert('Not eklenemedi: ' + e.message); return; }

    let updated = contact;
    if (!contact.last_contact_date || noteDate > contact.last_contact_date) {
      const { data, error: ue } = await supabase
        .from('directory_contacts')
        .update({ last_contact_date: noteDate, updated_at: new Date().toISOString() })
        .eq('id', contact.id)
        .select()
        .single();
      if (!ue && data) updated = data;
    }
    setNoteText('');
    setNoteDate(todayIso());
    setAddingNote(false);
    await loadNotes();
    if (updated !== contact) onContactUpdated(updated);
  };

  const isNetwork = contact.contact_kind === 'network';
  const stStyle = statusStyle(contact);
  const avStyle = avatarStyle(contact);
  const liHref = linkedinHref(contact.linkedin_url);
  const hasContactInfo = contact.email || contact.phone || contact.linkedin_url;

  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={{ maxWidth: 680, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <button className="btn-ghost" onClick={onBack} style={{ fontSize: 12, padding: '7px 12px' }}>
            ← Rehber
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {canEdit && (
              <button className="btn-ghost" onClick={() => onEdit(contact)} style={{ fontSize: 12, padding: '7px 14px' }}>
                Düzenle
              </button>
            )}
            {canDelete && (
              <button className="btn-danger" onClick={() => onDelete(contact)} style={{ fontSize: 12 }}>
                Sil
              </button>
            )}
          </div>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {/* 1 — Header */}
          <Section first>
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%', flexShrink: 0,
                background: avStyle.bg, color: avStyle.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, fontWeight: 700,
              }}>{initials(contact.full_name)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--font)' }}>
                  {contact.full_name}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 2 }}>
                  {[contact.profession, contact.company, contact.city_area].filter(Boolean).join(' · ')}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  <span style={{ ...BADGE, color: stStyle.color, background: stStyle.bg }}>
                    {statusLabel(contact)}
                  </span>
                  <span style={{ ...BADGE, color: 'var(--text2)', background: 'var(--bg3)' }}>
                    {INTRODUCED_BY[contact.introduced_by] || contact.introduced_by}
                  </span>
                  <span style={{ ...BADGE, color: 'var(--text2)', background: 'var(--bg3)' }}>
                    {CONTACT_KINDS[contact.contact_kind] || contact.contact_kind}
                  </span>
                </div>
              </div>
            </div>
          </Section>

          {/* 2 — Contact */}
          <Section title="İletişim">
            {hasContactInfo ? (
              <>
                {contact.email && (
                  <ContactRow icon="✉" label="Email">
                    <a href={`mailto:${contact.email}`} style={{ color: 'var(--blue)', textDecoration: 'none' }}>
                      {contact.email}
                    </a>
                  </ContactRow>
                )}
                {contact.phone && (
                  <ContactRow icon="☎" label="Telefon">
                    <a href={`tel:${contact.phone}`} style={{ color: 'var(--text)', textDecoration: 'none' }}>
                      {contact.phone}
                    </a>
                  </ContactRow>
                )}
                {contact.linkedin_url && (
                  <ContactRow icon="in" label="LinkedIn">
                    <a href={liHref} target="_blank" rel="noopener noreferrer"
                      style={{ color: 'var(--blue)', textDecoration: 'none' }}>
                      {contact.linkedin_url}
                    </a>
                  </ContactRow>
                )}
              </>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--text3)' }}>Kayıtlı iletişim bilgisi yok</div>
            )}
          </Section>

          {/* 3 — Relationship */}
          <Section title="Bize ilişkisi">
            <InfoRow label="Tanıştığımız tarih">
              {fmtDate(contact.first_met_date)}
              {contact.first_met_channel ? ` · ${contact.first_met_channel}` : ''}
            </InfoRow>
            <InfoRow label="Tanıtan">
              {INTRODUCED_BY[contact.introduced_by] || contact.introduced_by}
            </InfoRow>
            {contact.intro_detail && (
              <InfoRow label="Tanışma detayı">{contact.intro_detail}</InfoRow>
            )}
            <InfoRow label="Tipik cümle">
              {contact.typical_sentence || <span style={{ color: 'var(--text3)' }}>—</span>}
            </InfoRow>
            <InfoRow label="Bize faydası">
              {isNetwork
                ? (RELATIONSHIP_TYPES[contact.relationship_type] || '—')
                : 'Taşeron / Tedarikçi — iş verebiliriz'}
            </InfoRow>
            {contact.customer_id && (
              <InfoRow label="Müşteri kaydı">Bağlı</InfoRow>
            )}
            {contact.source && (
              <InfoRow label="Sisteme ekleyen">🤖 {contact.source}</InfoRow>
            )}
          </Section>

          {/* 4 — Next action */}
          <Section title="Sıradaki adım">
            {contact.next_action ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, alignItems: 'baseline' }}>
                <span style={{ fontSize: 13, color: 'var(--text)' }}>{contact.next_action}</span>
                <span style={{ fontSize: 13, color: 'var(--text2)', flexShrink: 0 }}>
                  {contact.next_action_date ? fmtDate(contact.next_action_date) : ''}
                </span>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--text3)' }}>
                Henüz tanımlı değil
                {canEdit && (
                  <button onClick={() => onEdit(contact)} style={{
                    background: 'none', border: 'none', color: 'var(--accent)',
                    fontSize: 13, padding: 0, marginLeft: 6, cursor: 'pointer',
                  }}>— ekle</button>
                )}
              </div>
            )}
          </Section>

          {/* 5 — Notes */}
          <Section title={`Notlar${notes.length ? ` · ${notes.length}` : ''}`}>
            {canEdit && (
              <div style={{
                background: 'var(--bg3)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius)', padding: 12, marginBottom: 14,
              }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input type="date" value={noteDate} onChange={e => setNoteDate(e.target.value)}
                    style={{ width: 150 }} />
                </div>
                <textarea rows={2} value={noteText} onChange={e => setNoteText(e.target.value)}
                  placeholder="Yeni not — ne konuştuk, ne oldu…"
                  style={{ resize: 'vertical', marginBottom: 8 }} />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="btn-primary" onClick={addNote}
                    disabled={addingNote || !noteText.trim()}
                    style={{ fontSize: 12, padding: '7px 14px' }}>
                    {addingNote ? 'Ekleniyor…' : 'Not ekle'}
                  </button>
                </div>
              </div>
            )}

            {loadingNotes ? (
              <div style={{ fontSize: 13, color: 'var(--text3)' }}>Yükleniyor…</div>
            ) : notes.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text3)' }}>Henüz not yok.</div>
            ) : (
              notes.map((n, i) => (
                <div key={n.id} style={{
                  padding: '10px 0', borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>
                    {fmtDate(n.note_date)}{n.created_by ? ` · ${n.created_by}` : ''}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                    {n.note_text}
                  </div>
                </div>
              ))
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}
